import { ReactNode, createContext, useContext } from 'react';
import { SentryError } from '@ifixit/sentry';
import { useInstantSearch } from '../hooks/use-instant-search';
import type { SearchClientType } from '../types';
import { Doctype } from '../types/indices';

/**
 * Search context type definition
 */
type SearchContextType = ReturnType<typeof useInstantSearch>;

/**
 * React Context for search functionality
 * This provides search state and functions to any component in the tree
 */
const SearchContext = createContext<SearchContextType | null>(null);

/**
 * Props for the SearchProvider component
 */
interface SearchProviderProps {
   /**
    * Configuration options for the search functionality
    */
   searchOptions: {
      /** The initialized Algolia search client */
      searchClient: SearchClientType;
      /** Record mapping document types to arrays of index names */
      indices: Partial<Record<Doctype, string[]>>;
      /** Optional language identifier for localized search */
      langid?: string;
   };
   /**
    * Child components that will have access to search context
    */
   children: ReactNode;
}

/**
 * SearchProvider component
 *
 * This component provides search functionality through React Context,
 * making search state and functions available to any descendant component.
 *
 * It's designed to be dynamically imported for better code splitting and performance.
 *
 * @example
 * ```tsx
 * <SearchProvider
 *   searchOptions={{
 *     searchClient: algoliaClient,
 *     indices: { products: ['products_index'] },
 *     langid: 'en'
 *   }}
 * >
 *   <SearchInput />
 *   <SearchResults />
 * </SearchProvider>
 * ```
 */
function SearchProvider({ searchOptions, children }: SearchProviderProps) {
   const searchState = useInstantSearch(searchOptions);

   return <SearchContext.Provider value={searchState}>{children}</SearchContext.Provider>;
}

/**
 * Custom hook to access search context
 *
 * This hook provides access to search functionality from any component
 * within a SearchProvider tree. Throws an error if used outside of a SearchProvider.
 *
 * @returns Search state and functions
 * @throws Error if used outside of SearchProvider
 *
 * @example
 * ```tsx
 * function MySearchComponent() {
 *   const { query, setQuery, results, isSearching } = useSearchContext();
 *
 *   return (
 *     <div>
 *       <input
 *         value={query}
 *         onChange={(e) => setQuery(e.target.value)}
 *       />
 *       {isSearching && <span>Searching...</span>}
 *       {results.products?.map(hit => (
 *         <div key={hit.objectID}>{hit.title}</div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSearchContext(): SearchContextType {
   const context = useContext(SearchContext);

   if (context === null) {
      throw new SentryError('useSearchContext must be used within a SearchProvider');
   }

   return context;
}

export default SearchProvider;
